import { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Card,
  message,
  Typography,
  Space,
  Tabs,
} from "antd";
import { supabase } from "../lib/supabase";
import { useUser } from "../hooks/useUser";
import { useTranslation } from "react-i18next";
import { UserEmailPreferences } from "../components/UserEmailPreferences";
import { AccountApiAccessTab } from "../components/account/AccountApiAccessTab";
import { useCompany } from "../hooks/useCompany";

interface EmailFormValues {
  email: string;
}

interface PasswordFormValues {
  newPassword: string;
  confirmPassword: string;
}

interface ProfileFormValues {
  firstName: string;
  lastName: string;
}

export const Account = () => {
  const { user, firstName, lastName } = useUser();
  const { companyDetails } = useCompany();
  const { t } = useTranslation();
  const [emailForm] = Form.useForm<EmailFormValues>();
  const [passwordForm] = Form.useForm<PasswordFormValues>();
  const [profileForm] = Form.useForm<ProfileFormValues>();
  const [emailLoading, setEmailLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);

  // Initialize form values when user data is loaded
  useEffect(() => {
    if (user) {
      // Set email form value
      emailForm.setFieldsValue({ email: user.email });

      // Set profile form values
      profileForm.setFieldsValue({
        firstName: firstName || "",
        lastName: lastName || "",
      });
    }
  }, [user, firstName, lastName, emailForm, profileForm]);

  const handleEmailUpdate = async (values: EmailFormValues) => {
    try {
      setEmailLoading(true);
      const { error } = await supabase.auth.updateUser({
        email: values.email,
      });

      if (error) throw error;

      message.success(t("account.emailUpdateSuccess"));
      emailForm.resetFields();
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : t("account.emailUpdateError")
      );
    } finally {
      setEmailLoading(false);
    }
  };

  const handlePasswordUpdate = async (values: PasswordFormValues) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error(t("account.passwordsDoNotMatch"));
      return;
    }

    try {
      setPasswordLoading(true);
      const { error } = await supabase.auth.updateUser({
        password: values.newPassword,
      });

      if (error) throw error;

      message.success(t("account.passwordUpdateSuccess"));
      passwordForm.resetFields();
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : t("account.passwordUpdateError")
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleProfileUpdate = async (values: ProfileFormValues) => {
    try {
      setProfileLoading(true);

      const { error } = await supabase.auth.updateUser({
        data: {
          first_name: values.firstName,
          last_name: values.lastName,
        },
      });

      if (error) throw error;

      message.success(t("account.profileUpdateSuccess"));

      // Get the updated user data to ensure the form shows the latest values
      const { data } = await supabase.auth.getUser();
      if (data?.user) {
        profileForm.setFieldsValue({
          firstName: data.user.user_metadata?.first_name || "",
          lastName: data.user.user_metadata?.last_name || "",
        });
      }
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : t("account.profileUpdateError")
      );
    } finally {
      setProfileLoading(false);
    }
  };

  const profileTab = (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Typography.Paragraph style={{ marginBottom: 0 }}>
        {t("common.email")}: {user?.email}
      </Typography.Paragraph>
      <Card title={t("account.profileSettings")}>
          <Form
            form={profileForm}
            layout="vertical"
            onFinish={handleProfileUpdate}
          >
            <Form.Item
              name="firstName"
              label={t("account.firstNameLabel")}
              rules={[
                { required: true, message: t("account.firstNameRequired") },
              ]}
            >
              <Input placeholder={t("account.enterFirstName")} />
            </Form.Item>

            <Form.Item
              name="lastName"
              label={t("account.lastNameLabel")}
              rules={[
                { required: true, message: t("account.lastNameRequired") },
              ]}
            >
              <Input placeholder={t("account.enterLastName")} />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={profileLoading}>
                {t("account.updateProfile")}
              </Button>
            </Form.Item>
          </Form>
      </Card>

      <Card title={t("account.emailSettings")}>
          <Form form={emailForm} layout="vertical" onFinish={handleEmailUpdate}>
            <Form.Item
              name="email"
              label={t("account.emailLabel")}
              rules={[
                { required: true, message: t("account.emailRequired") },
                { type: "email", message: t("account.validEmail") },
              ]}
            >
              <Input placeholder={t("account.enterNewEmail")} />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={emailLoading}>
                {t("account.updateEmail")}
              </Button>
            </Form.Item>
          </Form>
      </Card>

      <Card title={t("account.passwordSettings")}>
          <Form
            form={passwordForm}
            layout="vertical"
            onFinish={handlePasswordUpdate}
          >
            <Form.Item
              name="newPassword"
              label={t("account.newPasswordLabel")}
              rules={[
                { required: true, message: t("account.passwordRequired") },
                { min: 6, message: t("account.passwordMinLength") },
              ]}
            >
              <Input.Password placeholder={t("account.enterNewPassword")} />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              label={t("account.confirmPasswordLabel")}
              rules={[
                {
                  required: true,
                  message: t("account.confirmPasswordRequired"),
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("newPassword") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(t("account.passwordsDoNotMatch"))
                    );
                  },
                }),
              ]}
            >
              <Input.Password placeholder={t("account.confirmNewPassword")} />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={passwordLoading}
              >
                {t("account.updatePassword")}
              </Button>
            </Form.Item>
          </Form>
      </Card>

      <UserEmailPreferences />
    </Space>
  );

  return (
    <div style={{ maxWidth: 880, margin: "0 auto", padding: "24px" }}>
      <Typography.Title level={2}>{t("account.title")}</Typography.Title>

      <Tabs
        destroyInactiveTabPane
        items={[
          {
            key: "profile",
            label: t("account.profileTab"),
            children: profileTab,
          },
          ...(companyDetails?.api_access_granted
            ? [
                {
                  key: "api",
                  label: t("account.apiAccessTab"),
                  children: <AccountApiAccessTab />,
                },
              ]
            : []),
        ]}
      />
    </div>
  );
};
